import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router';
import { canAny } from '../../api/client';
import { fetchReport, reportFileUrl } from '../../api/ops';
import { useMe } from '../auth/session';
import { renderPdfPages, type PdfPages } from './pdfPages';

/** File name from a Content-Disposition header (RFC 5987 `filename*` first), else null. */
export function dispositionName(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star) return decodeURIComponent(star[1]!);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? decodeURIComponent(plain[1]!) : null;
}

/**
 * `/reports/view/:id` — the authority observer's ("AFM") report, shown as the page itself in the same tab, PDF only
 * (user, 2026-10-10): Back, Print, Download PDF and the document, drawn by pdf.js so Print prints the report alone. Only for portal accounts (no operations.view); the
 * other roles keep the file links of the reports pages and are sent back there.
 */
export function ReportViewPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const me = useMe().data;
  const portal = !!me && !canAny(me, 'operations.view');
  const report = useQuery({
    queryKey: ['reports', 'one', id],
    queryFn: () => fetchReport(id),
    enabled: portal,
  });
  const pagesBox = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<{ url: string; name: string; pages: PdfPages } | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!portal) return;
    let url: string | undefined;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(reportFileUrl(id, 'pdf'), { credentials: 'same-origin' });
        if (!res.ok) throw new Error(String(res.status));
        const name = dispositionName(res.headers.get('content-disposition')) ?? 'report.pdf';
        const blob = new Blob([await res.blob()], { type: 'application/pdf' });
        // the pages are drawn by the app (pdf.js), so the page can show and print the document alone
        const pages = await renderPdfPages(await blob.arrayBuffer());
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPdf({ url, name, pages });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, portal]);
  // the rendered canvases are attached as they are (no copy), once
  useEffect(() => {
    const box = pagesBox.current;
    if (!box || !pdf) return;
    box.replaceChildren(...pdf.pages.pages);
    return () => box.replaceChildren();
  }, [pdf]);

  if (me && !portal) return <Navigate to="/reports/reports" replace />;
  /**
   * Prints the report pages only: while printing, the application chrome is hidden (operations.css,
   * html.printing-report) and the paper takes the report's own page size, one canvas per sheet.
   */
  const print = () => {
    if (!pdf) return;
    const style = document.createElement('style');
    style.textContent = `@page { size: ${pdf.pages.width}pt ${pdf.pages.height}pt; margin: 0; }`;
    document.head.append(style);
    document.documentElement.classList.add('printing-report');
    const done = () => {
      window.removeEventListener('afterprint', done);
      document.documentElement.classList.remove('printing-report');
      style.remove();
    };
    window.addEventListener('afterprint', done);
    window.print();
  };
  return (
    <div className="page report-view-page">
      <div className="report-viewer-bar">
        <button type="button" className="btn btn-ghost" onClick={() => navigate('/reports/dashboard')}>
          <ArrowLeft size={15} /> {t('common.back')}
        </button>
        <strong className="ellipsis">{report.data?.title ?? pdf?.name ?? ''}</strong>
        <span className="toolbar">
          <button type="button" className="btn btn-primary" onClick={print} disabled={!pdf}>
            <Printer size={15} /> {t('reportsPage.viewer.print')}
          </button>
          {pdf ? (
            <a className="btn" href={pdf.url} download={pdf.name}>
              <Download size={15} /> {t('reportsPage.viewer.download')}
            </a>
          ) : (
            <button type="button" className="btn" disabled>
              <Download size={15} /> {t('reportsPage.viewer.download')}
            </button>
          )}
        </span>
      </div>
      {failed ? (
        <p className="form-error">{t('reportsPage.viewer.failed')}</p>
      ) : pdf ? (
        <div ref={pagesBox} className="report-pages" role="document" aria-label={pdf.name} />
      ) : (
        <p className="muted">{t('reportsPage.viewer.loading')}</p>
      )}
    </div>
  );
}
